/*
 * Copyright © 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import { useForm } from "../../provider/Form/FormProvider";
import React, { ReactNode, useEffect } from "react";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import { formValue } from "../../services/formServiceFunctions";
import FormHelperText from "@mui/material/FormHelperText";
import _get from "lodash/get";
import validatorStyle from "./validatorStyle";
import _find from "lodash/find";
import { FormChangeEvent, FormDataTypes } from "../../types/FormDataTypes";
import { ValidatorType } from "../../types/ValidatorType";

type ValidatorAutocompleteProps = {
  options: Array<any>;
  valueOptionAttribute?: string;
  noOptionsText?: string;
  name: string;
  label: string;
  helperText?: string | null;
  validators?: Array<ValidatorType>;
  dependentValidationFields?: Array<any>;
  fullWidth?: boolean;
  margin?: "dense" | "normal" | "none";
  renderOption?: (props?: any, options?: any, state?: any) => ReactNode;
};

export default function ValidatorAutocomplete({
  options,
  valueOptionAttribute = "label",
  noOptionsText = "Nicht gefunden",
  name,
  label,
  helperText,
  validators = [],
  dependentValidationFields = [],
  fullWidth = true,
  margin = "normal",
  ...rest
}: ValidatorAutocompleteProps) {
  const { values, errors, setError, handleChange, registerValidators }: FormDataTypes = useForm();
  const error = !!_get(errors, name);
  useEffect(() => {
    registerValidators(name, validators, dependentValidationFields);
    return () => {
      registerValidators(name, [], []);
      setError(name, undefined);
    };
  }, [name]);

  const handleAutoCompleteValueChange = (
    e: any,
    value: {
      [x: string]: any;
    }
  ) => {
    handleChange({
      target: {
        type: "text",
        name: name,
        value: value[valueOptionAttribute],
      },
    } as FormChangeEvent);
    setError(name, undefined);
  };

  return (
    <FormControl fullWidth={fullWidth} margin={margin} error={error}>
      <Autocomplete
        key={name}
        renderInput={(params) => <TextField {...params} label={label} sx={validatorStyle.textField} />}
        options={options}
        disableClearable
        value={_find(options, (option) => option[valueOptionAttribute] === formValue(values, name)) || null}
        onChange={handleAutoCompleteValueChange}
        noOptionsText={noOptionsText}
        {...rest}
      />
      <FormHelperText role={_get(errors, name) ? "alert" : undefined}>{_get(errors, name) || helperText}</FormHelperText>
    </FormControl>
  );
}
